import { ShoppingCart } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CopyValue } from '@/components/copy-value';
import { DataTable } from '@/components/data-table/data-table';
import { columnsFor } from '@/components/data-table/features';
import { PaginationBar } from '@/components/data-table/pagination-bar';
import { EmptyState } from '@/components/empty-state';
import { SearchInput } from '@/components/form/search-input';
import { SelectInput } from '@/components/form/select-input';
import { MoneyValue } from '@/components/money-value';
import { PageHeader } from '@/components/page-header';
import { ProductThumb } from '@/components/product-thumb';
import { QueryState } from '@/components/query-state';
import { Page } from '@/components/shell/page';
import { PageToolbar } from '@/components/shell/page-toolbar';
import { StatusBadge } from '@/components/status-badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useAuth } from '@/features/auth/use-auth';
import { ProfitBadge } from '@/features/catalog/components/profit-badge';
import {
  ALL_STAGES,
  MISSING_LABELS,
  type OrderFilter,
  type OrderPage,
  type OrderStage,
  type OrderSummary,
  STAGE_META,
  useOrderPlatforms,
  useOrders,
  useOrdersDashboard,
} from '../api/orders';
import { DeadlineBoard } from '../components/deadline-board';
import { PlatformMark } from '../components/platform-mark';
import { shipByLabel } from '../components/ship-by';

const COPY = {
  open: {
    title: 'Pedidos em aberto',
    description:
      'Da venda até a entrega, uma plataforma por aba, pelo horário de corte: o que vence antes sai antes.',
    empty: 'Nenhum pedido em aberto',
  },
  all: {
    title: 'Consultar pedidos',
    description: 'Todos os pedidos importados, com busca por número, SKU ou produto e período.',
    empty: 'Nenhum pedido encontrado',
  },
} as const;

/**
 * Pedidos das lojas (ADR-048), **separados por plataforma** (decisão do dono, módulos §0): uma aba
 * por plataforma com loja conectada; com mais de uma conta na mesma plataforma, escolhe-se a loja.
 * `open` é a operação (abertos até a entrega); `all` é a consulta.
 */
export function OrdersPage({ view }: { view: 'open' | 'all' }) {
  const platforms = useOrderPlatforms();
  const [provider, setProvider] = useState('');
  const current = provider || platforms.data?.[0]?.provider || '';
  const copy = COPY[view];

  return (
    <Page size="2xl">
      <PageHeader title={copy.title} description={copy.description} />
      <QueryState
        query={platforms}
        isEmpty={(data) => data.length === 0}
        empty={
          <EmptyState
            icon={ShoppingCart}
            title="Nenhuma loja com pedidos"
            description="Os pedidos aparecem quando uma loja de plataforma que envia pedidos é conectada."
          />
        }
      >
        {(data) => (
          <Tabs value={current} onValueChange={setProvider}>
            <TabsList>
              {data.map((platform) => (
                <TabsTrigger key={platform.provider} value={platform.provider} className="gap-2">
                  <PlatformMark provider={platform.provider} />
                  {platform.displayName}
                </TabsTrigger>
              ))}
            </TabsList>
            {/* Uma aba montada por vez: filtros e página de uma plataforma não vazam para outra. */}
            {data
              .filter((platform) => platform.provider === current)
              .map((platform) => (
                <PlatformOrders
                  key={platform.provider}
                  view={view}
                  provider={platform.provider}
                  stores={platform.stores}
                  emptyTitle={copy.empty}
                />
              ))}
          </Tabs>
        )}
      </QueryState>
    </Page>
  );
}

function PlatformOrders({
  view,
  provider,
  stores,
  emptyTitle,
}: {
  view: 'open' | 'all';
  provider: string;
  stores: { id: string; name: string }[];
  emptyTitle: string;
}) {
  const { can } = useAuth();
  const showMoney = can('financials.read');
  const [input, setInput] = useState('');
  const [filter, setFilter] = useState<OrderFilter>({ view, provider, page: 1 });
  const orders = useOrders(filter);
  const dashboard = useOrdersDashboard(provider, filter.connectionId);
  const change = (next: Partial<OrderFilter>) => setFilter((old) => ({ ...old, ...next, page: 1 }));

  // Espera a pessoa parar de digitar: uma consulta por pausa, não por tecla.
  useEffect(() => {
    const timer = setTimeout(
      () => setFilter((old) => ({ ...old, search: input || undefined, page: 1 })),
      300,
    );
    return () => clearTimeout(timer);
  }, [input]);

  const narrowed = Boolean(
    filter.search || filter.stage || filter.deadline || filter.from || filter.to,
  );
  return (
    <div className="mt-4 space-y-4">
      <PageToolbar>
        <SearchInput
          aria-label="Buscar por número do pedido, SKU ou produto"
          placeholder="Número do pedido, SKU ou produto"
          value={input}
          onChange={(event) => setInput(event.target.value)}
        />
        {stores.length > 1 && (
          <SelectInput
            aria-label="Loja"
            className="w-auto min-w-48"
            value={filter.connectionId ?? ''}
            emptyLabel="Todas as lojas"
            options={stores.map((store) => ({ value: store.id, label: store.name }))}
            onValueChange={(next) => change({ connectionId: next || undefined })}
          />
        )}
        {view === 'all' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Input
              type="date"
              aria-label="Vendas a partir de"
              className="w-auto"
              value={filter.from ?? ''}
              onChange={(event) => change({ from: event.target.value || undefined })}
            />
            até
            <Input
              type="date"
              aria-label="Vendas até"
              className="w-auto"
              value={filter.to ?? ''}
              onChange={(event) => change({ to: event.target.value || undefined })}
            />
          </div>
        )}
      </PageToolbar>

      {view === 'open' && dashboard.data && (
        <DeadlineBoard
          data={dashboard.data}
          value={
            filter.deadline
              ? { deadline: filter.deadline }
              : filter.stage
                ? { stage: filter.stage }
                : null
          }
          onChange={(selection) =>
            change({ deadline: selection?.deadline, stage: selection?.stage })
          }
        />
      )}

      {/* Consulta: situações numa coluna ao lado da tabela, que no topo quebravam em duas linhas. */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {view === 'all' && orders.data && (
          <aside className="lg:sticky lg:top-4 lg:w-56 lg:shrink-0">
            <StageFilter
              stages={ALL_STAGES}
              counts={orders.data.stageCounts}
              value={filter.stage}
              onChange={(stage) => change({ stage })}
            />
          </aside>
        )}
        <div className="min-w-0 flex-1 space-y-4">
          <Card>
            <CardContent className="pt-4 sm:pt-6">
              <QueryState
                query={orders}
                isEmpty={(data) => data.items.length === 0}
                empty={
                  <EmptyState
                    icon={ShoppingCart}
                    title={narrowed ? 'Nada encontrado' : emptyTitle}
                    description={
                      narrowed
                        ? 'Tente outro termo, outra situação ou outro período.'
                        : 'Venda nova aparece aqui em segundos, pelo aviso da plataforma.'
                    }
                  />
                }
              >
                {(data: OrderPage) => (
                  <OrdersTable
                    orders={data.items}
                    showMoney={showMoney}
                    showDeadline={view === 'open'}
                  />
                )}
              </QueryState>
            </CardContent>
          </Card>

          {orders.data && (
            <PaginationBar
              page={filter.page}
              pageSize={orders.data.pageSize}
              total={orders.data.total}
              onPageChange={(page) => setFilter((old) => ({ ...old, page }))}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/** Situações com quantos pedidos há em cada uma, contados no servidor com a busca aplicada. */
function StageFilter({
  stages,
  counts,
  value,
  onChange,
}: {
  stages: OrderStage[];
  counts: Record<OrderStage, number>;
  value: OrderStage | undefined;
  onChange: (stage: OrderStage | undefined) => void;
}) {
  return (
    <ToggleGroup
      type="single"
      variant="outline"
      aria-label="Filtrar por situação"
      value={value ?? 'ALL'}
      onValueChange={(next) => onChange(!next || next === 'ALL' ? undefined : (next as OrderStage))}
      // Coluna no desktop (uma situação por linha, a contagem à direita); no celular, em linha.
      orientation="vertical"
      spacing={1}
      className="w-full flex-wrap lg:flex-col lg:items-stretch"
    >
      <ToggleGroupItem value="ALL" className="lg:w-full lg:justify-between">
        Todas
      </ToggleGroupItem>
      {stages.map((stage) => (
        <ToggleGroupItem key={stage} value={stage} className="lg:w-full lg:justify-between">
          {STAGE_META[stage].label}
          <span className="tabular text-muted-foreground">{counts[stage]}</span>
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

/** Sem ordenar pela coluna: a lista vem paginada e ordenada no servidor (abertos: mais antigo primeiro). */
const orderColumn = columnsFor<OrderSummary>();

function OrdersTable({
  orders,
  showMoney,
  showDeadline,
}: {
  orders: OrderSummary[];
  showMoney: boolean;
  /** Em aberto: o horário de corte de cada pacote, em destaque. */
  showDeadline: boolean;
}) {
  const now = new Date();
  const columns = [
    orderColumn.accessor('externalId', {
      enableSorting: false,
      header: 'Pedido',
      meta: { className: 'whitespace-nowrap' },
      cell: ({ row }) => (
        <div className="space-y-0.5">
          <CopyValue value={row.original.externalId} label="número do pedido" />
          <p className="text-xs text-muted-foreground">
            {new Date(row.original.orderedAt).toLocaleString('pt-BR', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </p>
        </div>
      ),
    }),
    orderColumn.accessor('items', {
      enableSorting: false,
      header: 'Produto',
      meta: { className: 'whitespace-normal' },
      cell: ({ row }) => {
        const [first, ...rest] = row.original.items;
        return (
          <Link to={`/pedidos/${row.original.id}`} className="flex items-center gap-3">
            <ProductThumb src={first?.coverUrl ?? null} />
            <span className="space-y-0.5">
              <span className="block">
                {first ? `${first.quantity}× ${first.title}` : 'Pedido sem itens'}
                {rest.length > 0 && (
                  <span className="text-muted-foreground"> e mais {rest.length}</span>
                )}
              </span>
              {first?.sku && (
                <span className="block font-mono text-xs text-muted-foreground">{first.sku}</span>
              )}
            </span>
          </Link>
        );
      },
    }),
    orderColumn.accessor('stage', {
      enableSorting: false,
      header: 'Situação',
      cell: ({ row }) => (
        <StatusBadge tone={STAGE_META[row.original.stage].tone}>
          {STAGE_META[row.original.stage].label}
        </StatusBadge>
      ),
    }),
    ...(showDeadline
      ? [
          orderColumn.accessor('shipment', {
            id: 'shipBy',
            enableSorting: false,
            header: 'Enviar até',
            meta: { className: 'whitespace-nowrap' },
            cell: ({ row }) => {
              const shipBy =
                row.original.stage === 'TO_SHIP' ? row.original.shipment?.shipBy : null;
              if (!shipBy) return <span className="text-muted-foreground">—</span>;
              const deadline = shipByLabel(shipBy, now);
              return (
                <div className="space-y-0.5">
                  <StatusBadge tone={deadline.tone}>{deadline.label}</StatusBadge>
                  {deadline.detail && (
                    <p className="text-xs text-muted-foreground">{deadline.detail}</p>
                  )}
                </div>
              );
            },
          }),
        ]
      : []),
    orderColumn.accessor('recipient', {
      enableSorting: false,
      header: 'Cliente',
      meta: { hideBelow: 'md', className: 'whitespace-normal' },
      cell: ({ row }) => {
        const recipient = row.original.recipient;
        if (!recipient) return <span className="text-muted-foreground">—</span>;
        const place = [recipient.city, recipient.state].filter(Boolean).join('/');
        return (
          <div>
            {recipient.name ?? '—'}
            {place && <span className="block text-xs text-muted-foreground">{place}</span>}
          </div>
        );
      },
    }),
    orderColumn.accessor('store', {
      enableSorting: false,
      header: 'Loja',
      meta: { hideBelow: 'lg' },
      cell: ({ row }) => row.original.store.name,
    }),
    orderColumn.accessor('totalAmount', {
      enableSorting: false,
      header: 'Valor',
      meta: { align: 'right' },
      cell: ({ row }) => <MoneyValue value={row.original.totalAmount} />,
    }),
    ...(showMoney
      ? [
          orderColumn.accessor('realized', {
            enableSorting: false,
            header: 'Resultado',
            meta: { align: 'right' },
            cell: ({ row }) => <RealizedCell order={row.original} />,
          }),
        ]
      : []),
  ];
  return <DataTable data={orders} columns={columns} getRowId={(order) => order.id} />;
}

/** Resultado da venda e o selo nas faixas da empresa; a dica diz o que falta e se é aproximado. */
function RealizedCell({ order }: { order: OrderSummary }) {
  const realized = order.realized;
  if (!realized || realized.status === 'CANCELLED' || !realized.view) {
    return <span className="text-muted-foreground">—</span>;
  }
  // Sem o custo da mercadoria não há resultado a mostrar — só o selo, como no catálogo.
  const noCost = realized.view.level === 'NO_COST';
  return (
    <div className="flex flex-col items-end gap-1">
      <ProfitBadge
        view={realized.view}
        {...(realized.margin ? { margin: realized.margin } : {})}
        detail={
          <>
            {!noCost && (
              <p>
                Resultado: <MoneyValue value={realized.result} />
              </p>
            )}
            {realized.missing.length > 0 && (
              <p>Falta: {realized.missing.map((code) => MISSING_LABELS[code]).join(', ')}</p>
            )}
            {realized.approximate && <p>Com valores de hoje (custo ou embalagem): aproximado.</p>}
          </>
        }
      />
      {!noCost && <MoneyValue value={realized.result} className="text-xs text-muted-foreground" />}
    </div>
  );
}
